import React, {
  useState,
  useLayoutEffect,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { StyleSheet, Text, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import { UserCard, CallOffer, Typography, ErrorDialog } from "src/components";
import {
  useOffer,
  useUser,
  useCognito,
  useActiveCall,
  useCallActions,
  useTwilioToken,
} from "src/hooks";
import {
  Actions,
  ActionsLayout,
  BannerLayout,
  Comment,
  TalkActions,
  WaitingAnswerBlock,
  TalkBlock,
} from "./components";
import { RequestStatus, IRequest } from "src/domains";
import KeepAwake from "@sayem314/react-native-keep-awake";
import { EventRegister } from "react-native-event-listeners";
import { ChatMessageEvent } from "src/utils/event";
import LocalText from "src/local";
import alert from "src/components/alert";
// import { PubSub } from "@aws-amplify/pubsub";
import { useNavigation } from "@react-navigation/native";
import { RootStackScreenProps } from "src/navigation/types";

import axios from "axios";

import "@livekit/components-styles";
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useLocalParticipant,
  useMediaDevices,
  useTracks,
  VideoTrack,
} from "@livekit/components-react";
import { Track, VideoCaptureOptions, VideoPresets } from "livekit-client";

const serverUrl = "wss://choku-t0vue653.livekit.cloud";

export type CallNavigationParam = undefined;

export enum CallLayout {
  Unknown,
  WaitingAnswer,
  WaitingAnswerCancel,
  AnswerRequired,
  WaitingTalkConnectionBySender,
  WaitingTalkConnectionByReceiver,
  Talk,
}

export const CallScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"Call">["navigation"]>();
  const [cognitoUsername] = useCognito();
  const [call] = useActiveCall();

  const [layout, setLayout] = useState<CallLayout>(CallLayout.Unknown);

  const [, setError] = useState<Error | undefined>(undefined);
  const [twilioToken] = useTwilioToken(call?.serviceId);
  const isTwilioConnected = useRef<boolean>(false);

  const [isNewMessagesExists, setIsNewMessagesExists] =
    useState<boolean>(false);

  const [finishCallActionLoading, setFinishCallActionLoading] =
    useState<boolean>(false);

  const [token, setToken] = useState<string | undefined>();

  const { requestShowCall, finishCall } = useCallActions();

  const twilioConnect = useCallback(
    (callId: string, token: string) => {
      if (!cognitoUsername) {
        console.log("cognitio username not found");
      }
      console.log("Add code for twilioConnect..."); // FIXME
      axios
        .get(
          `https://i6pdyptyq7.execute-api.ap-northeast-1.amazonaws.com/room/${callId}/${cognitoUsername}` // FIXME: add to env
        )
        .then((r) => {
          setToken(r.data.token as string);
        })
        .catch((e) => console.error(e));
    },
    [cognitoUsername]
  );

  useEffect(() => {
    requestShowCall();
  }, [requestShowCall]);

  useEffect(() => {
    const unsubscribe = navigation.addListener("focus", (e) => {
      setIsNewMessagesExists(false);
    });
    return () => {
      unsubscribe();
    };
  }, [navigation]);

  useEffect(() => {
    if (call) {
      if (call.status === RequestStatus.WAITING_OPPONENT) {
        setLayout(
          call.senderId === cognitoUsername
            ? CallLayout.WaitingAnswer
            : CallLayout.AnswerRequired
        );
      } else if (call.status === RequestStatus.APPROVED_BY_OPPONENET) {
        setLayout(
          call.senderId === cognitoUsername
            ? CallLayout.WaitingTalkConnectionBySender
            : CallLayout.WaitingTalkConnectionByReceiver
        );
      } else {
        setLayout(CallLayout.Talk);
      }
    } else {
      navigation.goBack();
    }
  }, [call, navigation, cognitoUsername]);

  const twilioConnectHandler = useCallback(() => {
    if (!twilioToken || !call) {
      return;
    }
    isTwilioConnected.current = true;
    twilioConnect(call.id, twilioToken);
  }, [twilioToken, call, twilioConnect]);

  useEffect(() => {
    if (
      !twilioToken ||
      !call ||
      layout !== CallLayout.Talk ||
      isTwilioConnected.current
    ) {
      return;
    }
    twilioConnectHandler();
  }, [layout, twilioToken, call, twilioConnectHandler]);

  const twilioDisconnect = useCallback(() => {
    console.log("Add code for twilioDisconnect"); // FIXME
    // if (localVideoTrack) {
    //   const localTrackPublication =
    //     room?.localParticipant?.unpublishTrack(localVideoTrack);
    //   // TODO: remove when SDK implements this event. See: https://issues.corp.twilio.com/browse/JSDK-2592
    //   room?.localParticipant?.emit("trackUnpublished", localTrackPublication);

    //   localVideoTrack.stop();
    //   setLocalVideoTrack(undefined);
    // }

    // room?.localParticipant.audioTracks.forEach((publication) => {
    //   publication.track.stop();
    //   publication.unpublish();
    // });

    // room?.localParticipant.videoTracks.forEach((publication) => {
    //   publication.track.stop();
    //   publication.unpublish();
    // });

    // room?.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      twilioDisconnect();
    };
  }, [twilioDisconnect]);

  useEffect(() => {
    const chatMessageListener = EventRegister.addEventListener(
      ChatMessageEvent,
      (e) => {
        if (e.conversationId === call?.id) {
          setIsNewMessagesExists(true);
        }
      }
    );
    return () => {
      EventRegister.removeEventListener(chatMessageListener as string);
    };
  }, [call]);

  const finishCallHandler = useCallback(async () => {
    if (!call) {
      return;
    }
    setFinishCallActionLoading(true);
    try {
      await finishCall({ id: call.id });
    } catch (e: any) {
      console.error(e);
      if (e.errors && Array.isArray(e.errors)) {
        setError(new Error(e.errors[0].message));
      } else {
        setError(e as Error);
      }
    }
    setFinishCallActionLoading(false);
  }, [call, finishCall]);

  // const pingPongInterval = useRef<any>(null);
  // const pongTime = useRef<number>(0);

  // const pingHandler = useCallback(
  //   (data: any) => {
  //     if (
  //       data.type === "ping" &&
  //       cognitoUsername &&
  //       data.from !== cognitoUsername
  //     ) {
  //       pongTime.current = Date.now();
  //     }
  //   },
  //   [cognitoUsername]
  // );
  // const ping = useCallback(() => {
  //   if (call && cognitoUsername) {
  //     if (call.status === RequestStatus.CALL_APPROVED) {
  //       if (!pongTime.current) {
  //         pongTime.current = Date.now();
  //       }
  //       PubSub.publish(call.id, {
  //         type: "ping",
  //         from: cognitoUsername,
  //       });

  //       if (Date.now() - pongTime.current > 5000) {
  //         finishCallHandler();
  //       }
  //     }
  //   }
  // }, [call, cognitoUsername, finishCallHandler]);
  // useEffect(() => {
  //   const mqttSubscribtion = call?.id
  //     ? PubSub.subscribe(call?.id).subscribe({
  //         next: (data) => pingHandler(data.value),
  //         error: (error) => console.error(error),
  //         complete: () => console.log("Done"),
  //       })
  //     : undefined;

  //   pingPongInterval.current = setInterval(() => ping(), 1000);
  //   return () => {
  //     mqttSubscribtion?.unsubscribe();
  //     clearInterval(pingPongInterval.current);
  //   };
  // }, [call, pingHandler, ping]);

  if (!call) {
    return (
      <View style={styles.root}>
        <Text>{LocalText.screens.call.callOver}</Text>
      </View>
    );
  }

  return (
    <LiveKitRoom
      video={false}
      audio={true}
      token={token}
      serverUrl={serverUrl}
      data-lk-theme="default"
      style={{ flex: 1, display: "flex" }}
    >
      <Room
        call={call}
        cognitoUsername={cognitoUsername}
        isNewMessagesExists={isNewMessagesExists}
        finishCallActionLoading={finishCallActionLoading}
        layout={layout}
        finishCallHandler={finishCallHandler}
        onResetNewMessageFlag={() => setIsNewMessagesExists(false)}
        onChangeLayout={(newLayout) => setLayout(newLayout)}
      />
    </LiveKitRoom>
  );
};

type RoomProps = {
  call?: IRequest;
  cognitoUsername?: string;
  isNewMessagesExists: boolean;
  finishCallActionLoading: boolean;
  layout: CallLayout;
  finishCallHandler: () => void;
  onResetNewMessageFlag: () => void;
  onChangeLayout: (layout: CallLayout) => void;
};
const Room = ({
  call,
  cognitoUsername,
  isNewMessagesExists,
  finishCallActionLoading,
  layout,
  finishCallHandler,
  onResetNewMessageFlag,
  onChangeLayout,
}: RoomProps) => {
  const navigation =
    useNavigation<RootStackScreenProps<"Call">["navigation"]>();
  const [offer] = useOffer(call?.serviceId);
  const [user] = useUser(
    call?.senderId === cognitoUsername ? call?.opponentId : call?.senderId
  );
  const [error, setError] = useState<Error | undefined>(undefined);
  const [comment, setComment] = useState<string | undefined>();
  const [cancelCallRequestActionLoading, setCancelCallRequestActionLoading] =
    useState<boolean>(false);
  const [rejectCallRequestActionLoading, setRejectCallRequestActionLoading] =
    useState<boolean>(false);
  const [approveCallRequestActionLoading, setApproveCallRequestActionLoading] =
    useState<boolean>(false);
  const [approveCallActionLoading, setApproveCallActionLoading] =
    useState<boolean>(false);
  const [rejectCallActionLoading, setRejectCallActionLoading] =
    useState<boolean>(false);

  const {
    requestHideCall,
    cancelCallRequest,
    rejectCallRequest,
    approveCallRequest,
    approveCall,
    rejectCall,
  } = useCallActions();

  const isSpeakerEnable = false; // FIXME
  const {
    localParticipant,
    isMicrophoneEnabled,
    isCameraEnabled,
    lastCameraError,
    lastMicrophoneError,
  } = useLocalParticipant();
  // const participants = useRemoteParticipants();/
  const videoDevices = useMediaDevices({ kind: "videoinput" });
  // const audioOutputDevices = useMediaDevices({ kind: "audiooutput" });
  const isCameraConntected = videoDevices.length > 0;

  useLayoutEffect(() => {
    if (isCameraEnabled) {
      navigation.setOptions({ headerShown: false });
    } else {
      navigation.setOptions({
        title: LocalText.screens.call.navigationBarTitle,
        headerShown: true,
      });
    }
  }, [navigation, isCameraEnabled]);

  useEffect(() => {
    console.log("lastCameraError", lastCameraError);
  }, [lastCameraError]);

  useEffect(() => {
    console.log("lastMicrophoneError", lastMicrophoneError);
  }, [lastMicrophoneError]);

  const cancelCallRequestHandler = useCallback(async () => {
    if (!call) {
      return;
    }
    setCancelCallRequestActionLoading(true);
    try {
      await cancelCallRequest({ id: call.id, offerCancelMessage: comment });
    } catch (e: any) {
      console.error(e);
      if (e.errors && Array.isArray(e.errors)) {
        setError(new Error(e.errors[0].message));
      } else {
        setError(e as Error);
      }
    }
    setCancelCallRequestActionLoading(false);
  }, [call, comment, cancelCallRequest]);
  const rejectCallRequestHandler = useCallback(async () => {
    if (!call) {
      return;
    }
    setRejectCallRequestActionLoading(true);
    try {
      await rejectCallRequest({ id: call.id, offerRejectMessage: comment });
    } catch (e: any) {
      console.error(e);
      if (e.errors && Array.isArray(e.errors)) {
        setError(new Error(e.errors[0].message));
      } else {
        setError(e as Error);
      }
    }
    setRejectCallRequestActionLoading(false);
  }, [call, comment, rejectCallRequest]);
  const approveCallRequestHandler = useCallback(async () => {
    if (!call) {
      return;
    }
    setApproveCallRequestActionLoading(true);
    try {
      await approveCallRequest({
        id: call.id,
        requestTimestamp: "" + Date.now(),
      });
    } catch (e: any) {
      console.error(e);
      if (e.errors && Array.isArray(e.errors)) {
        setError(new Error(e.errors[0].message));
      } else {
        setError(e as Error);
      }
    }
    setApproveCallRequestActionLoading(false);
  }, [call, approveCallRequest]);
  const approveCallHandler = useCallback(async () => {
    if (!call) {
      return;
    }
    setApproveCallActionLoading(true);
    await approveCall({ id: call.id });
    setApproveCallActionLoading(false);
  }, [call, approveCall]);
  const rejectSenderCallHandler = useCallback(async () => {
    if (!call) {
      return;
    }
    setRejectCallActionLoading(true);
    await rejectCall({ id: call.id });
    setRejectCallActionLoading(false);
  }, [call, rejectCall]);
  const rejectReceiverCallHandler = useCallback(async () => {
    if (!call) {
      return;
    }
    setRejectCallActionLoading(true);
    try {
      await rejectCall({ id: call.id });
    } catch (e) {
      console.error(e);
    }
    setRejectCallActionLoading(false);
  }, [call, rejectCall]);

  const cameraTurnOnHandler = useCallback(() => {
    localParticipant.setCameraEnabled(true);
  }, [localParticipant]);
  const cameraTurnOffHandler = useCallback(() => {
    localParticipant.setCameraEnabled(false);
  }, [localParticipant]);
  const micTurnOnHandler = useCallback(() => {
    localParticipant.setMicrophoneEnabled(true);
  }, [localParticipant]);
  const micTurnOffHandler = useCallback(() => {
    localParticipant.setMicrophoneEnabled(false);
  }, [localParticipant]);

  // const audioTracks = useTracks([Track.Source.Microphone]);

  // useEffect(() => {
  //   console.log("audioTracks", audioTracks);
  // }, [audioTracks]);

  // useEffect(() => {
  //   console.log("audioOutputDevices", audioOutputDevices);
  // }, [audioOutputDevices]);

  const speakerHandler = useCallback(() => {
    console.log("Add code for speakerHandler..."); // FIXME
    // // FIXME
    // // twilioRef.current?.toggleSoundSetup(true);
    // setIsSpeakerEnable(true);
    // participants.forEach(participant => {
    //   participant.setAudioOutput({deviceId: ""})

    // })
    // console.log("audioOutputDevices", audioOutputDevices);
  }, []);
  const phoneHandler = useCallback(() => {
    console.log("Add code for phoneHandler..."); // FIXME
    // // FIXME
    // // twilioRef.current?.toggleSoundSetup(false);
    // setIsSpeakerEnable(false);
  }, []);

  const tracks = useTracks([Track.Source.Camera]);
  const localTrack = tracks.find((track) => track.participant.isLocal);
  const remoteTracks = tracks.filter((track) => !track.participant.isLocal);

  const [isFrontFacing, setFrontFacing] = useState<boolean>(false);
  const flipCameraHandler = useCallback(() => {
    const videoPub = localParticipant.getTrackPublication(Track.Source.Camera);
    if (!videoPub) {
      return;
    }

    const options: VideoCaptureOptions = {
      resolution: VideoPresets.h720.resolution,
      facingMode: isFrontFacing ? "user" : "environment",
    };
    videoPub.videoTrack?.restartTrack(options);
    setFrontFacing((v) => !v);
  }, [localParticipant, isFrontFacing]);

  return (
    <View style={styles.root}>
      <RoomAudioRenderer />
      <View
        style={[
          styles.content,
          styles.contentBlack,
          { display: isCameraEnabled ? "flex" : "none" },
        ]}
      >
        {remoteTracks.map((track) => (
          <VideoTrack key={track.publication.trackSid} trackRef={track} />
        ))}
        {localTrack && (
          <VideoTrack
            style={{
              flex: 1,
              width: 100,
              height: 166,
              position: "absolute",
              right: 10,
              top: 0,
              opacity: 1,
              backgroundColor: "white",
            }}
            trackRef={localTrack}
          />
        )}
        {call && (
          <TalkActions
            containerStyle={styles.talkActionsVideo}
            isMicEnable={isMicrophoneEnabled}
            isSpeakerEnable={isSpeakerEnable}
            isCameraEnable={isCameraEnabled}
            isNewMessageIndicator={isNewMessagesExists}
            isCameraConntected={isCameraConntected}
            isFlipEnabled={videoDevices.length > 1}
            onMicTurnOn={micTurnOnHandler}
            onMicTurnOff={micTurnOffHandler}
            onSpeaker={speakerHandler}
            onPhone={phoneHandler}
            onCameraTurnOn={cameraTurnOnHandler}
            onCameraTurnOff={cameraTurnOffHandler}
            onChat={() => navigation.push("CallChat", { callId: call.id })}
            onFlipCamera={() => flipCameraHandler()}
          />
        )}
      </View>

      {!isCameraEnabled ? (
        <View style={styles.content}>
          <ScrollView
            style={styles.scrollView}
            showsVerticalScrollIndicator={false}
          >
            {user ? (
              <UserCard
                contanerStyle={styles.user}
                source={user?.image ? { uri: user?.image } : undefined}
                name={user?.name}
                birthday={user?.birthday}
                gender={user?.gender}
                prefecture={user?.address?.prefecture}
                isVerified={user?.identificationStatus === "APPROVED"}
                onPress={() =>
                  navigation.push("User", {
                    id: user.id,
                    title: "",
                    hideStatus: true,
                  })
                }
              />
            ) : null}
            {offer ? (
              <React.Fragment>
                <Typography
                  text={LocalText.screens.call.service}
                  variant={"section"}
                />
                <CallOffer offer={offer} hideStatus={true} />
              </React.Fragment>
            ) : null}
            {[CallLayout.WaitingAnswer, CallLayout.WaitingAnswerCancel].indexOf(
              layout
            ) !== -1 ? (
              call ? (
                <WaitingAnswerBlock
                  call={call}
                  bannerType={BannerLayout.WaitingAnswer}
                />
              ) : null
            ) : null}
            {[CallLayout.AnswerRequired].indexOf(layout) !== -1 ? (
              call ? (
                <WaitingAnswerBlock
                  call={call}
                  bannerType={BannerLayout.AnswerRequired}
                  comment={call.message}
                />
              ) : null
            ) : null}
            {[CallLayout.WaitingTalkConnectionBySender].indexOf(layout) !==
            -1 ? (
              call ? (
                <WaitingAnswerBlock
                  call={call}
                  bannerType={BannerLayout.WaitingTalkConnectionBySender}
                  comment={call.message}
                />
              ) : null
            ) : null}
            {[CallLayout.WaitingTalkConnectionByReceiver].indexOf(layout) !==
            -1 ? (
              call ? (
                <WaitingAnswerBlock
                  call={call}
                  bannerType={BannerLayout.WaitingTalkConnectionByReceiver}
                />
              ) : null
            ) : null}
            {[CallLayout.Talk].indexOf(layout) !== -1 ? (
              call ? (
                <TalkBlock call={call} />
              ) : null
            ) : null}
            {[
              CallLayout.WaitingAnswerCancel,
              CallLayout.AnswerRequired,
            ].indexOf(layout) !== -1 ? (
              <Comment
                containerStyle={styles.comment}
                value={comment}
                onChange={setComment}
              />
            ) : null}
          </ScrollView>
          {[CallLayout.Talk].indexOf(layout) !== -1 ? (
            call ? (
              <TalkActions
                containerStyle={styles.talkActions}
                isMicEnable={isMicrophoneEnabled}
                isSpeakerEnable={isSpeakerEnable}
                isCameraEnable={isCameraEnabled}
                isCameraConntected={isCameraConntected}
                isNewMessageIndicator={isNewMessagesExists}
                onMicTurnOn={micTurnOnHandler}
                onMicTurnOff={micTurnOffHandler}
                onSpeaker={speakerHandler}
                onPhone={phoneHandler}
                onCameraTurnOn={cameraTurnOnHandler}
                onCameraTurnOff={cameraTurnOffHandler}
                onChat={() => {
                  onResetNewMessageFlag();
                  navigation.push("CallChat", { callId: call.id });
                }}
              />
            ) : null
          ) : null}
        </View>
      ) : null}
      {layout === CallLayout.WaitingAnswer ? (
        <Actions
          layout={ActionsLayout.WaitingAnswer}
          onLeft={() => {
            requestHideCall();
            navigation.goBack();
          }}
          onRight={() => onChangeLayout(CallLayout.WaitingAnswerCancel)}
        />
      ) : null}
      {layout === CallLayout.WaitingAnswerCancel ? (
        <Actions
          layout={ActionsLayout.WaitingAnswerCancel}
          onLeft={() => onChangeLayout(CallLayout.WaitingAnswer)}
          rightBtnLoading={cancelCallRequestActionLoading}
          onRight={() => cancelCallRequestHandler()}
        />
      ) : null}
      {layout === CallLayout.AnswerRequired ? (
        <Actions
          layout={ActionsLayout.AnswerRequired}
          leftBtnLoading={rejectCallRequestActionLoading}
          onLeft={() =>
            alert(
              LocalText.screens.call.abortAlertTitle,
              `${user?.name || "unknown"}${
                LocalText.screens.call.abortAlertDescription
              }`,
              [
                {
                  text: LocalText.common.yes,
                  onPress: rejectCallRequestHandler,
                },
                { text: LocalText.common.no },
              ]
            )
          }
          rightBtnLoading={approveCallRequestActionLoading}
          onRight={() => approveCallRequestHandler()}
        />
      ) : null}
      {layout === CallLayout.WaitingTalkConnectionBySender ? (
        <Actions
          layout={ActionsLayout.WaitingTalkConnectionBySender}
          leftBtnLoading={rejectCallActionLoading}
          onLeft={() => rejectSenderCallHandler()}
          rightBtnLoading={approveCallActionLoading}
          onRight={() => approveCallHandler()}
        />
      ) : null}
      {layout === CallLayout.WaitingTalkConnectionByReceiver ? (
        <Actions
          layout={ActionsLayout.WaitingTalkConnectionByReceiver}
          leftBtnLoading={rejectCallActionLoading}
          onLeft={() => rejectReceiverCallHandler()}
        />
      ) : null}
      {layout === CallLayout.Talk ? (
        <Actions
          layout={ActionsLayout.Talk}
          leftBtnLoading={finishCallActionLoading}
          onLeft={() => finishCallHandler()}
        />
      ) : null}
      {error ? (
        <ErrorDialog
          error={error}
          visible={true}
          onClose={() => setError(undefined)}
        />
      ) : null}
      <KeepAwake />
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.white,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  contentBlack: {
    backgroundColor: colors.chokuBlack,
  },
  user: {
    marginTop: 16,
    marginBottom: 8,
    marginHorizontal: 16,
  },
  comment: {
    marginVertical: 8,
  },
  talkActions: {
    // position: 'absolute',
    // left: 0,
    // right: 0,
    // bottom: 0,
    marginHorizontal: 16,
    marginBottom: 32,
    paddingTop: 36,
  },
  talkActionsVideo: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: 16,
    paddingBottom: 32,
  },
});
